<template>
  <div class="user-profile-container">
    <div class="max-w-4xl mx-auto p-6">
      <!-- Tarjeta de perfil principal -->
      <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <!-- Header con portada -->
        <div class="relative h-48 bg-gradient-to-r from-primary-500 to-primary-700 dark:from-primary-600 dark:to-primary-800">
          <div class="absolute -bottom-16 left-8">
            <div class="relative">
              <!-- Avatar grande -->
              <div class="w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 bg-white dark:bg-gray-800 overflow-hidden shadow-xl">
                <img
                  v-if="userData.fotoPerfilUrl && !imageError"
                  :src="userData.fotoPerfilUrl"
                  class="w-full h-full object-cover"
                  alt="Foto de perfil"
                  @error="handleImageError"
                />
                <div 
                  v-else
                  class="w-full h-full flex items-center justify-center"
                  :style="{ backgroundColor: avatarColor }"
                >
                  <span class="text-4xl font-bold text-white">{{ avatarInitials }}</span>
                </div>
              </div>
              
              <!-- Botón de editar perfil -->
              <button 
                v-if="isOwnProfile"
                @click="editProfile"
                class="absolute bottom-0 right-0 bg-white dark:bg-gray-700 rounded-full p-2 shadow-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <Icon icon="mdi:pencil" class="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>
        </div>

        <!-- Información del usuario -->
        <div class="pt-20 px-8 pb-8">
          <div class="flex justify-between items-start mb-6">
            <div>
              <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
                {{ userData.nombre }} {{ userData.apellido }}
              </h1>
              <p class="text-gray-600 dark:text-gray-400 mt-1">
                {{ formatRole(userData.nombreRol) }}
                <span v-if="userData.especialidad" class="ml-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm">
                  {{ userData.especialidad }}
                </span>
              </p>
              <div class="flex items-center mt-2 text-gray-500 dark:text-gray-400">
                <Icon icon="mdi:email" class="w-4 h-4 mr-1" />
                <span class="text-sm">{{ userData.email }}</span>
              </div>
              <div v-if="userData.telefono" class="flex items-center mt-1 text-gray-500 dark:text-gray-400">
                <Icon icon="mdi:phone" class="w-4 h-4 mr-1" />
                <span class="text-sm">{{ userData.telefono }}</span>
              </div>
              <div v-if="userData.nombreUsuario" class="flex items-center mt-1 text-gray-500 dark:text-gray-400">
                <Icon icon="mdi:account" class="w-4 h-4 mr-1" />
                <span class="text-sm">@{{ userData.nombreUsuario }}</span>
              </div>
            </div>
            
            <!-- Estado del usuario -->
            <div class="flex flex-col items-end">
              <span 
                :class="{
                  'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': userData.estado === 'Activo',
                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': userData.estado !== 'Activo'
                }"
                class="px-3 py-1 rounded-full text-sm font-medium"
              >
                {{ userData.estado || 'Activo' }}
              </span>
              <span class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                ID: {{ userData.idUsuario }}
              </span>
            </div>
          </div>

          <!-- Resumen de estadísticas por períodos -->
          <div class="mt-4">
            <!-- Selector de período -->
            <div class="flex gap-2 mb-4">
              <button 
                v-for="periodo in periodos"
                :key="periodo.value"
                @click="seleccionarPeriodo(periodo.value)"
                class="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                :class="periodoSeleccionado === periodo.value 
                  ? 'bg-primary-500 text-white' 
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'"
              >
                {{ periodo.label }}
              </button>
            </div>

            <!-- Tarjetas de estadísticas por período -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div class="text-center bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                <div class="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {{ estadisticasPeriodo.total || 0 }}
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">Total {{ esTecnico ? 'Reparaciones' : 'Ventas' }}</div>
              </div>
              <div class="text-center bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                <div class="text-3xl font-bold text-green-600 dark:text-green-400">
                  ${{ estadisticasPeriodo.totalFacturado || 0 }}
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">Total Facturado</div>
              </div>
              <div class="text-center bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                <div class="text-3xl font-bold text-purple-600 dark:text-purple-400">
                  ${{ estadisticasPeriodo.promedio || 0 }}
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">Promedio por {{ esTecnico ? 'orden' : 'venta' }}</div>
              </div>
              <div class="text-center bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4">
                <div class="text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {{ estadisticasPeriodo.completados || 0 }}
                </div>
                <div class="text-sm text-gray-600 dark:text-gray-400">{{ esTecnico ? 'Entregados' : 'Completadas' }}</div>
              </div>
            </div>
          </div>

          <!-- Estadísticas del usuario - según su rol (resumen general) -->
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
            <div class="text-center bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
              <div class="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {{ stats.totalItems || 0 }}
              </div>
              <div class="text-sm text-gray-600 dark:text-gray-400">{{ stats.labelTotal }}</div>
            </div>
            <div class="text-center bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
              <div class="text-3xl font-bold text-green-600 dark:text-green-400">
                {{ stats.completados || 0 }}
              </div>
              <div class="text-sm text-gray-600 dark:text-gray-400">{{ stats.labelCompletados }}</div>
            </div>
            <div class="text-center bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4">
              <div class="text-3xl font-bold text-yellow-600 dark:text-yellow-400">
                {{ stats.enProceso || 0 }}
              </div>
              <div class="text-sm text-gray-600 dark:text-gray-400">{{ stats.labelEnProceso }}</div>
            </div>
            <div class="text-center bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
              <div class="text-3xl font-bold text-purple-600 dark:text-purple-400">
                ${{ stats.totalFacturado || 0 }}
              </div>
              <div class="text-sm text-gray-600 dark:text-gray-400">{{ stats.labelTotalFacturado }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Información adicional según el rol -->
      <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Items recientes (reparaciones o ventas según rol) -->
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Icon :icon="stats.iconoItems" class="w-6 h-6 mr-2 text-primary-500" />
            {{ stats.tituloItemsRecientes }}
          </h2>
          <div v-if="loadingData" class="text-center py-8">
            <Icon icon="mdi:loading" class="w-8 h-8 animate-spin text-primary-500 mx-auto" />
            <p class="text-sm text-gray-500 mt-2">Cargando...</p>
          </div>
          <div v-else-if="itemsRecientes.length > 0" class="space-y-3 max-h-96 overflow-y-auto">
            <div v-for="(item, index) in itemsRecientes" :key="index" class="flex items-start space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors border-b border-gray-100 dark:border-gray-700">
              <div class="w-10 h-10 rounded-full flex items-center justify-center" :class="item.colorClass">
                <Icon :icon="item.icono" class="w-5 h-5" />
              </div>
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <div>
                    <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ item.titulo }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400">{{ item.descripcion }}</p>
                    <p v-if="item.cliente" class="text-xs text-gray-400 mt-1">Cliente: {{ item.cliente }}</p>
                  </div>
                  <div class="text-right">
                    <span class="text-xs font-bold text-green-600">${{ item.monto }}</span>
                    <span class="ml-2 px-2 py-0.5 rounded-full text-[9px] font-bold" :class="item.estadoClass">
                      {{ item.estadoTexto }}
                    </span>
                  </div>
                </div>
                <p class="text-[10px] text-gray-400 mt-1">{{ item.fecha }}</p>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
            <Icon :icon="stats.iconoItems" class="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{{ stats.mensajeVacio }}</p>
          </div>
        </div>

        <!-- Estadísticas detalladas -->
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Icon icon="mdi:chart-line" class="w-6 h-6 mr-2 text-primary-500" />
            Estadísticas Detalladas
          </h2>
          
          <div v-if="loadingData" class="text-center py-8">
            <Icon icon="mdi:loading" class="w-8 h-8 animate-spin text-primary-500 mx-auto" />
            <p class="text-sm text-gray-500 mt-2">Cargando estadísticas...</p>
          </div>
          
          <div v-else>
            <!-- Barras de progreso -->
            <div class="space-y-4">
              <div>
                <div class="flex justify-between text-sm mb-1">
                  <span class="text-gray-600 dark:text-gray-400">{{ stats.labelCompletados }}</span>
                  <span class="font-semibold text-gray-900 dark:text-white">{{ stats.completados || 0 }}</span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div class="bg-green-500 rounded-full h-2" :style="{ width: porcentajeCompletados + '%' }"></div>
                </div>
              </div>
              
              <div>
                <div class="flex justify-between text-sm mb-1">
                  <span class="text-gray-600 dark:text-gray-400">{{ stats.labelEnProceso }}</span>
                  <span class="font-semibold text-gray-900 dark:text-white">{{ stats.enProceso || 0 }}</span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div class="bg-yellow-500 rounded-full h-2" :style="{ width: porcentajeEnProceso + '%' }"></div>
                </div>
              </div>
              
              <div>
                <div class="flex justify-between text-sm mb-1">
                  <span class="text-gray-600 dark:text-gray-400">{{ stats.labelPendientes }}</span>
                  <span class="font-semibold text-gray-900 dark:text-white">{{ stats.pendientes || 0 }}</span>
                </div>
                <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div class="bg-gray-500 rounded-full h-2" :style="{ width: porcentajePendientes + '%' }"></div>
                </div>
              </div>
            </div>
            
            <!-- Promedio -->
            <div class="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600 dark:text-gray-400">{{ stats.labelPromedio }}</span>
                <span class="text-xl font-bold text-primary-600 dark:text-primary-400">${{ stats.promedioFacturado || 0 }}</span>
              </div>
            </div>
            
            <!-- Miembro desde -->
            <div class="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div class="flex justify-between items-center">
                <span class="text-sm text-gray-600 dark:text-gray-400">Miembro desde</span>
                <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ stats.miembroDesde || 'No disponible' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Acciones del perfil -->
      <div class="mt-6 flex justify-end space-x-3">
        <button 
          v-if="isOwnProfile"
          @click="changePassword"
          class="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center"
        >
          <Icon icon="mdi:key" class="w-5 h-5 mr-2" />
          Cambiar Contraseña
        </button>
        <button 
          v-if="isOwnProfile"
          @click="editProfile"
          class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center"
        >
          <Icon icon="mdi:pencil" class="w-5 h-5 mr-2" />
          Editar Perfil
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { apiFetch, getCurrentServer } from '@/api/index.js';
import * as recepcionApi from '@/api/recepcionEquipo.js';
import * as ventasApi from '@/api/ventas.js';

export default {
  name: 'UserProfile',
  components: {
    Icon,
  },
  props: {
    userId: {
      type: [Number, String],
      default: null
    },
    isOwnProfile: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      userData: {
        idUsuario: null,
        nombre: '',
        apellido: '',
        email: '',
        nombreUsuario: '',
        nombreRol: '',
        especialidad: '',
        telefono: '',
        estado: 'Activo',
        fotoPerfilUrl: null
      },
      stats: {
        totalItems: 0,
        completados: 0,
        enProceso: 0,
        pendientes: 0,
        totalFacturado: 0,
        promedioFacturado: 0,
        miembroDesde: '',
        // Textos dinámicos según el rol
        labelTotal: 'Total',
        labelCompletados: 'Completados',
        labelEnProceso: 'En Proceso',
        labelPendientes: 'Pendientes',
        labelTotalFacturado: 'Total Facturado',
        labelPromedio: 'Promedio por item',
        tituloItemsRecientes: 'Actividad Reciente',
        iconoItems: 'mdi:history',
        mensajeVacio: 'No hay actividad registrada'
      },
      itemsRecientes: [],
      todasReparaciones: [],
      todasVentas: [],
      imageError: false,
      loadingData: false,
      esTecnico: false,
      esVendedor: false,
      
      // Nuevos datos para estadísticas por período
      periodoSeleccionado: 'diario',
      periodos: [
        { value: 'diario', label: 'Diario' },
        { value: 'mensual', label: 'Mensual' },
        { value: 'total', label: 'Total' }
      ],
      estadisticasPeriodo: {
        total: 0,
        totalFacturado: 0,
        promedio: 0,
        completados: 0
      }
    };
  },
  computed: {
    avatarInitials() {
      if (!this.userData.nombre && !this.userData.apellido) return 'U';
      const first = this.userData.nombre ? this.userData.nombre.charAt(0) : '';
      const last = this.userData.apellido ? this.userData.apellido.charAt(0) : '';
      return (first + last).toUpperCase() || 'U';
    },
    
    avatarColor() {
      const initials = this.avatarInitials;
      if (!initials) return '#4F46E5';
      
      const colors = [
        '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
        '#EC4899', '#6366F1', '#06B6D4', '#84CC16', '#F97316'
      ];
      
      let hash = 0;
      for (let i = 0; i < initials.length; i++) {
        hash = initials.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      const index = Math.abs(hash) % colors.length;
      return colors[index];
    },
    
    fullName() {
      return `${this.userData.nombre || ''} ${this.userData.apellido || ''}`.trim();
    },
    
    porcentajeCompletados() {
      if (this.stats.totalItems === 0) return 0;
      return (this.stats.completados / this.stats.totalItems) * 100;
    },
    
    porcentajeEnProceso() {
      if (this.stats.totalItems === 0) return 0;
      return (this.stats.enProceso / this.stats.totalItems) * 100;
    },
    
    porcentajePendientes() {
      if (this.stats.totalItems === 0) return 0;
      return (this.stats.pendientes / this.stats.totalItems) * 100;
    }
  },
  methods: {
    async fetchUserImageUrl(usuarioId) {
      try {
        const imagenes = await apiFetch('/Imagen/listar/usuario');
        const imagenUsuario = imagenes.find(img => img.entidadId === usuarioId);
        
        if (imagenUsuario) {
          return imagenUsuario.url;
        }
      } catch (error) {
        console.error('❌ Error obteniendo imagen:', error);
      }
      return null;
    },
    
    async loadUserData() {
      try {
        let userData = null;
        
        // Cargar el usuario logueado desde localStorage
        const userNewStr = localStorage.getItem('user');
        if (userNewStr) {
          userData = JSON.parse(userNewStr);
        } else {
          const userOldStr = localStorage.getItem('user_data');
          if (userOldStr) {
            userData = JSON.parse(userOldStr);
          }
        }
        
        if (userData) {
          const usuarioId = userData.idUsuario || userData.id;
          const imagenUrl = await this.fetchUserImageUrl(usuarioId);
          
          this.userData = {
            idUsuario: usuarioId,
            nombre: userData.nombre || '',
            apellido: userData.apellido || '',
            email: userData.email || '',
            nombreUsuario: userData.nombreUsuario || userData.username || '',
            nombreRol: userData.nombreRol || userData.rol?.nombre || 'Usuario',
            especialidad: userData.especialidad || '',
            telefono: userData.telefono || '',
            estado: userData.estado || 'Activo',
            fotoPerfilUrl: imagenUrl
          };
          
          if (!this.userData.fotoPerfilUrl && userData.fotoPerfil) {
            this.userData.fotoPerfilUrl = `${getCurrentServer()}/imagenes/usuario/${userData.fotoPerfil}`;
          }
          
          // Determinar rol del usuario
          const rol = (this.userData.nombreRol || '').toLowerCase();
          this.esTecnico = rol.includes('tecnico') || rol.includes('técnico');
          this.esVendedor = rol.includes('vendedor') || rol.includes('vendedora');
          
          // Cargar datos según el rol
          if (this.esTecnico) {
            this.configurarParaTecnico();
            await this.cargarReparacionesDelTecnico(usuarioId);
          } else if (this.esVendedor) {
            this.configurarParaVendedor();
            await this.cargarVentasDelVendedor(usuarioId);
          } else {
            // Administrador u otro rol: no tiene reparaciones ni ventas
            this.configurarParaAdministrador();
            this.stats.totalItems = 0;
            this.stats.completados = 0;
            this.stats.enProceso = 0;
            this.stats.pendientes = 0;
            this.stats.totalFacturado = 0;
            this.stats.promedioFacturado = 0;
            this.stats.miembroDesde = this.formatearFechaCreacion(userData.fecha_creacion || userData.fechaCreacion);
            this.itemsRecientes = [];
          }
          
          // Calcular estadísticas por período después de cargar los datos
          this.calcularEstadisticasPeriodo();
        }
      } catch (error) {
        console.error('❌ Error cargando datos del usuario:', error);
      }
    },
    
    configurarParaTecnico() {
      this.stats.labelTotal = 'Órdenes Atendidas';
      this.stats.labelCompletados = 'Entregadas';
      this.stats.labelEnProceso = 'En Proceso';
      this.stats.labelPendientes = 'Pendientes';
      this.stats.labelTotalFacturado = 'Total Facturado';
      this.stats.labelPromedio = 'Promedio por orden';
      this.stats.tituloItemsRecientes = 'Últimas Reparaciones';
      this.stats.iconoItems = 'mdi:tools';
      this.stats.mensajeVacio = 'No hay reparaciones registradas';
    },
    
    configurarParaVendedor() {
      this.stats.labelTotal = 'Ventas Realizadas';
      this.stats.labelCompletados = 'Completadas';
      this.stats.labelEnProceso = 'Créditos Activos';
      this.stats.labelPendientes = 'Pendientes';
      this.stats.labelTotalFacturado = 'Total Vendido';
      this.stats.labelPromedio = 'Ticket Promedio';
      this.stats.tituloItemsRecientes = 'Últimas Ventas';
      this.stats.iconoItems = 'mdi:cart';
      this.stats.mensajeVacio = 'No hay ventas registradas';
    },
    
    configurarParaAdministrador() {
      this.stats.labelTotal = 'Total Realizado';
      this.stats.labelCompletados = 'Completados';
      this.stats.labelEnProceso = 'En Proceso';
      this.stats.labelPendientes = 'Pendientes';
      this.stats.labelTotalFacturado = 'Total Facturado';
      this.stats.labelPromedio = 'Promedio';
      this.stats.tituloItemsRecientes = 'Actividad Reciente';
      this.stats.iconoItems = 'mdi:account';
      this.stats.mensajeVacio = 'No hay actividad registrada para este usuario';
    },
    
    async cargarReparacionesDelTecnico(usuarioId) {
      this.loadingData = true;
      try {
        console.log(`🔍 Buscando reparaciones para el técnico ID: ${usuarioId}`);
        
        const res = await recepcionApi.obtenerRecepciones();
        
        if (res.success && res.data) {
          // Filtrar SOLO las órdenes donde este técnico aparece como reparador o asignado
          const reparaciones = res.data.filter(orden => {
            const idTecnicoReparador = orden.idTecnicoReparador || orden.IdTecnicoReparador;
            const idTecnicoAsignado = orden.idTecnicoAsignado || orden.IdTecnicoAsignado;
            
            const esReparador = parseInt(idTecnicoReparador) === parseInt(usuarioId);
            const esAsignado = parseInt(idTecnicoAsignado) === parseInt(usuarioId);
            
            return esReparador || esAsignado;
          });
          
          this.todasReparaciones = reparaciones;
          this.calcularEstadisticasReparaciones(reparaciones);
        }
      } catch (error) {
        console.error('❌ Error cargando reparaciones:', error);
      } finally {
        this.loadingData = false;
      }
    },
    
    async cargarVentasDelVendedor(usuarioId) {
      this.loadingData = true;
      try {
        console.log(`🔍 Buscando ventas para el vendedor ID: ${usuarioId}`);
        
        // Usar la función específica de ventas por vendedor
        const res = await ventasApi.obtenerVentasPorVendedor(usuarioId);
        
        console.log(`📊 Resultado ventas:`, res);
        
        if (res.success && res.data && res.data.length > 0) {
          this.todasVentas = res.data;
          this.calcularEstadisticasVentas(res.data);
        } else {
          // Si no hay ventas, mostrar todo en 0
          this.todasVentas = [];
          this.stats.totalItems = 0;
          this.stats.completados = 0;
          this.stats.enProceso = 0;
          this.stats.pendientes = 0;
          this.stats.totalFacturado = 0;
          this.stats.promedioFacturado = 0;
          this.stats.miembroDesde = this.formatearFechaCreacion(new Date().toISOString());
          this.itemsRecientes = [];
        }
      } catch (error) {
        console.error('❌ Error cargando ventas:', error);
        this.todasVentas = [];
        this.stats.totalItems = 0;
        this.stats.completados = 0;
        this.stats.enProceso = 0;
        this.stats.pendientes = 0;
        this.stats.totalFacturado = 0;
        this.stats.promedioFacturado = 0;
        this.itemsRecientes = [];
      } finally {
        this.loadingData = false;
      }
    },
    
    calcularEstadisticasReparaciones(reparaciones) {
      const completadas = reparaciones.filter(o => {
        const estado = (o.nombreEstado || '').toUpperCase();
        return estado.includes('ENTREGADO') || estado.includes('COMPLETADO');
      }).length;
      
      const enProceso = reparaciones.filter(o => {
        const estado = (o.nombreEstado || '').toUpperCase();
        return estado.includes('REPARADO') || estado.includes('LISTO') || estado.includes('EN PROCESO');
      }).length;
      
      const pendientes = reparaciones.length - completadas - enProceso;
      
      const totalFacturado = reparaciones.reduce((sum, o) => sum + (parseFloat(o.precioTotalUsd) || 0), 0);
      
      this.stats.totalItems = reparaciones.length;
      this.stats.completados = completadas;
      this.stats.enProceso = enProceso;
      this.stats.pendientes = pendientes;
      this.stats.totalFacturado = totalFacturado.toFixed(2);
      this.stats.promedioFacturado = reparaciones.length > 0 ? (totalFacturado / reparaciones.length).toFixed(2) : 0;
      this.stats.miembroDesde = this.formatearMiembroDesde(reparaciones);
      
      // Últimas 5 reparaciones
      this.itemsRecientes = [...reparaciones]
        .sort((a, b) => new Date(b.fechaReparacion || b.fechaIngreso || 0) - new Date(a.fechaReparacion || a.fechaIngreso || 0))
        .slice(0, 5)
        .map(r => ({
          titulo: `Orden #${r.numeroOrden}`,
          descripcion: r.descripcion?.substring(0, 40) || 'Sin descripción',
          cliente: r.nombreCliente,
          monto: (r.precioTotalUsd || 0).toFixed(2),
          fecha: this.formatearFechaLarga(r.fechaReparacion || r.fechaIngreso),
          estadoTexto: r.nombreEstado || 'PENDIENTE',
          icono: 'mdi:wrench',
          colorClass: this.getEstadoColorClass(r.nombreEstado),
          estadoClass: this.getEstadoBadgeClass(r.nombreEstado)
        }));
    },
    
    calcularEstadisticasVentas(ventas) {
      const completadas = ventas.filter(v => v.estado?.toLowerCase() === 'completada' || v.estado?.toLowerCase() === 'activa').length;
      const enProceso = ventas.filter(v => v.tipoVenta?.toLowerCase() === 'credito' && v.estado?.toLowerCase() !== 'completada' && v.estado?.toLowerCase() !== 'activa').length;
      const pendientes = ventas.length - completadas - enProceso;
      
      const totalFacturado = ventas.reduce((sum, v) => sum + (parseFloat(v.totalUsd) || 0), 0);
      
      this.stats.totalItems = ventas.length;
      this.stats.completados = completadas;
      this.stats.enProceso = enProceso;
      this.stats.pendientes = pendientes;
      this.stats.totalFacturado = totalFacturado.toFixed(2);
      this.stats.promedioFacturado = ventas.length > 0 ? (totalFacturado / ventas.length).toFixed(2) : 0;
      this.stats.miembroDesde = this.formatearMiembroDesdeVentas(ventas);
      
      // Últimas 5 ventas
      this.itemsRecientes = [...ventas]
        .sort((a, b) => new Date(b.fechaVenta || 0) - new Date(a.fechaVenta || 0))
        .slice(0, 5)
        .map(v => ({
          titulo: `Factura #${v.numeroFactura}`,
          descripcion: `${v.cantidadTotal || 0} productos`,
          cliente: v.clienteNombre,
          monto: (v.totalUsd || 0).toFixed(2),
          fecha: this.formatearFechaLarga(v.fechaVenta),
          estadoTexto: v.estado || 'COMPLETADA',
          icono: 'mdi:cart',
          colorClass: 'bg-green-100 text-green-600',
          estadoClass: v.tipoVenta === 'credito' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
        }));
    },
    
    // NUEVO: Calcular estadísticas por período
    calcularEstadisticasPeriodo() {
      const items = this.esTecnico ? this.todasReparaciones : this.todasVentas;
      
      if (!items || items.length === 0) {
        this.estadisticasPeriodo = {
          total: 0,
          totalFacturado: 0,
          promedio: 0,
          completados: 0
        };
        return;
      }
      
      const ahora = new Date();
      let itemsFiltrados = [];
      
      // Filtrar según el período seleccionado
      if (this.periodoSeleccionado === 'diario') {
        const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
        itemsFiltrados = items.filter(item => {
          const fecha = new Date(this.obtenerFecha(item));
          return fecha >= hoy;
        });
      } else if (this.periodoSeleccionado === 'mensual') {
        const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
        itemsFiltrados = items.filter(item => {
          const fecha = new Date(this.obtenerFecha(item));
          return fecha >= inicioMes;
        });
      } else {
        // Total: todos los items
        itemsFiltrados = items;
      }
      
      // Calcular estadísticas
      const total = itemsFiltrados.length;
      const totalFacturado = itemsFiltrados.reduce((sum, item) => {
        const monto = this.esTecnico ? parseFloat(item.precioTotalUsd) : parseFloat(item.totalUsd);
        return sum + (monto || 0);
      }, 0);
      
      const completados = itemsFiltrados.filter(item => {
        if (this.esTecnico) {
          const estado = (item.nombreEstado || '').toUpperCase();
          return estado.includes('ENTREGADO') || estado.includes('COMPLETADO');
        } else {
          return item.estado?.toLowerCase() === 'completada' || item.estado?.toLowerCase() === 'activa';
        }
      }).length;
      
      this.estadisticasPeriodo = {
        total,
        totalFacturado: totalFacturado.toFixed(2),
        promedio: total > 0 ? (totalFacturado / total).toFixed(2) : 0,
        completados
      };
    },
    
    obtenerFecha(item) {
      if (this.esTecnico) {
        return item.fechaReparacion || item.fechaIngreso || new Date().toISOString();
      } else {
        return item.fechaVenta || new Date().toISOString();
      }
    },
    
    seleccionarPeriodo(periodo) {
      this.periodoSeleccionado = periodo;
      this.calcularEstadisticasPeriodo();
    },
    
    formatearFechaCreacion(fecha) {
      if (!fecha) return 'Nuevo';
      try {
        const fechaObj = new Date(fecha);
        return fechaObj.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' });
      } catch {
        return 'Nuevo';
      }
    },
    
    getEstadoColorClass(estado) {
      const estadoUpper = (estado || '').toUpperCase();
      if (estadoUpper.includes('ENTREGADO') || estadoUpper.includes('COMPLETADO')) {
        return 'bg-green-100 text-green-600';
      }
      if (estadoUpper.includes('REPARADO') || estadoUpper.includes('LISTO')) {
        return 'bg-yellow-100 text-yellow-600';
      }
      if (estadoUpper.includes('EN PROCESO')) {
        return 'bg-blue-100 text-blue-600';
      }
      return 'bg-gray-100 text-gray-600';
    },
    
    getEstadoBadgeClass(estado) {
      const estadoUpper = (estado || '').toUpperCase();
      if (estadoUpper.includes('ENTREGADO') || estadoUpper.includes('COMPLETADO')) {
        return 'bg-green-100 text-green-700';
      }
      if (estadoUpper.includes('REPARADO') || estadoUpper.includes('LISTO')) {
        return 'bg-yellow-100 text-yellow-700';
      }
      if (estadoUpper.includes('EN PROCESO')) {
        return 'bg-blue-100 text-blue-700';
      }
      return 'bg-gray-100 text-gray-600';
    },
    
    formatearMiembroDesde(reparaciones) {
      if (!reparaciones || reparaciones.length === 0) return 'Nuevo';
      const fechas = reparaciones.map(r => r.fechaIngreso).filter(f => f);
      if (fechas.length === 0) return 'Nuevo';
      const fechaMin = new Date(Math.min(...fechas.map(f => new Date(f))));
      return fechaMin.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' });
    },
    
    formatearMiembroDesdeVentas(ventas) {
      if (!ventas || ventas.length === 0) return 'Nuevo';
      const fechas = ventas.map(v => v.fechaVenta).filter(f => f);
      if (fechas.length === 0) return 'Nuevo';
      const fechaMin = new Date(Math.min(...fechas.map(f => new Date(f))));
      return fechaMin.toLocaleDateString('es-ES', { year: 'numeric', month: 'long' });
    },
    
    formatearFechaLarga(fechaISO) {
      if (!fechaISO) return 'Fecha no disponible';
      try {
        const fecha = new Date(fechaISO);
        return fecha.toLocaleDateString('es-ES', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });
      } catch {
        return fechaISO;
      }
    },
    
    handleImageError() {
      this.imageError = true;
      this.userData.fotoPerfilUrl = null;
    },
    
    formatRole(role) {
      if (!role) return 'Usuario';
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    },
    
    editProfile() {
      this.$router.push('/editar-perfil');
      this.$emit('editProfile');
    },
    
    changePassword() {
      this.$router.push('/cambiar-contrasena');
      this.$emit('changePassword');
    }
  },
  mounted() {
    this.loadUserData();
    
    // Escuchar cambios en localStorage
    window.addEventListener('storage', (event) => {
      if (event.key === 'user' || event.key === 'user_data') {
        this.loadUserData();
      }
    });
    
    // Escuchar evento de actualización de usuario
    window.addEventListener('userUpdated', this.loadUserData);
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.loadUserData);
    window.removeEventListener('userUpdated', this.loadUserData);
  }
};
</script>

<style scoped>
.user-profile-container {
  min-height: 100vh;
  background-color: #f9fafb;
}

.dark .user-profile-container {
  background-color: #111827;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.bg-white, .dark\:bg-gray-800 {
  animation: fadeIn 0.5s ease-out;
}

@media (max-width: 768px) {
  .user-profile-container {
    padding: 0;
  }
  
  .max-w-4xl {
    padding: 1rem;
  }
  
  .h-48 {
    height: 120px;
  }
  
  .w-32 {
    width: 80px;
    height: 80px;
  }
  
  .absolute.-bottom-16 {
    bottom: -40px;
  }
}
</style>